import {Injectable} from '@angular/core';
import saveAs from 'file-saver';
import {Utils} from '../utils/utils';
import {GeometryConverter} from '../utils/geometry-converter';
import {Layer} from '../models/layer';
import {ProjectExportData} from '../../common/model/project-export-data';
import {ProjectData} from '../../common/model/project-data';
import {buildMmp, MmpProjectSettings} from './mmp-format';
import {ObjectUtil} from '../../common/utils/object.util';

/**
 * Writing a project to an .mmp file.
 *
 * The file format itself is in mmp-format.ts, shared with the server so every
 * .mmp -- the export API's, the projects API's, this Save's -- is the same
 * file. This class only moves the canvas models out through
 * GeometryConverter's `*ToData`. Reading an .mmp into a project is the
 * server's (POST /import, functions' client/manager/import), as for every app.
 */
@Injectable({
  providedIn: 'root'
})
export class MmpLoader {

  // ── Writing ──────────────────────────────────────────────────────────────

  /** The project's settings that travel in the file. */
  static projectSettingsOf(project: ProjectData | null | undefined): MmpProjectSettings {
    if (!project) {
      return {};
    }
    return {
      taxRate: typeof project.tax_rate === 'number' ? project.tax_rate : undefined,
      clientName: project.client_name || undefined,
      defaultProductId: project.default_product_id || undefined,
    };
  }

  /** An .mmp of canvas models (the open project, or a selection of it). */
  static buildFromModels(data: ProjectExportData): string {
    return buildMmp({
      projectId: data.projectId,
      projectName: data.projectName,
      geometries: [
        ...(data.polygons || []).map(p => GeometryConverter.polygonToData(p, true)),
        ...(data.spots || []).map(s => GeometryConverter.spotToData(s)),
      ],
      groups: (data.groups || []).map(g => MmpLoader.groupToData(g)),
      layers: (data.layers || []).map(l => MmpLoader.layerToData(l)),
      project: data.project,
    });
  }

  /** An .mmp of stored documents, as read from the database (an array or a map by id). */
  static buildFromData(options: {
    geometries: any; layers?: Layer[]; groups?: any[];
    projectId?: string; projectName?: string; project?: MmpProjectSettings;
  }): string {
    const geometries = Array.isArray(options.geometries)
      ? options.geometries : ObjectUtil.toArray(options.geometries || {});
    return buildMmp({
      projectId: options.projectId,
      projectName: options.projectName,
      geometries,
      groups: (options.groups || []).map(g => MmpLoader.groupToData(g)),
      layers: (options.layers || []).map(l => MmpLoader.layerToData(l)),
      project: options.project,
    });
  }

  /** Downloads an .mmp of canvas models. */
  static save(data: ProjectExportData): Promise<void> {
    return MmpLoader.download(MmpLoader.buildFromModels(data), data.projectName);
  }

  /** Downloads an .mmp of stored documents. */
  static saveFromData(geoData: any, layers: Layer[], pid: string, fileName: string,
                      extra: {groups?: any[], project?: MmpProjectSettings} = {}): Promise<void> {
    const xml = MmpLoader.buildFromData({
      geometries: geoData, layers, groups: extra.groups, projectId: pid, projectName: fileName, project: extra.project,
    });
    return MmpLoader.download(xml, fileName);
  }

  static download(xml: string, name: string): Promise<void> {
    saveAs(new Blob([xml], {type: 'application/xml;charset=utf-8'}),
      (Utils.removeSpecialChars(name || 'project') || 'project') + '.mmp');
    return Promise.resolve();
  }

  private static groupToData(g: any): any {
    return {
      id: g.id,
      name: g.name,
      description: g.description,
      color: g.color,
      showBounds: !!g.showBounds,
    };
  }

  private static layerToData(l: any): any {
    return {
      id: l.id,
      type: l.type,
      name: l.name,
      opacity: l.opacity,
      bounds: l.bounds,
      downloadUrl: l.downloadUrl,
      featureCount: l.featureCount,
    };
  }
}
